import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@core-ui/Components/ui/button';
import { Download, Filter, RefreshCw, AlertTriangle } from 'lucide-react';
import { IconBug } from '@tabler/icons-react';
import { SearchInput } from '@ui/shared/searchInput';
import type { ListSource } from '@features/contacts';
import useTimeAgo from '@hooks/useTimeAgo.ts';
import { LoadingState } from '@features/contacts/types/contacts.types.ts';

interface ContactsHeaderProps {
  contactListTitle: string | null;
  contactsCount: number;
  warningsCount: number;
  searchQuery: string;
  onSearchQueryChange: (searchValue: string) => void;
  onClearSearch: () => void;
  activeSource: ListSource;
  onExportContacts: () => void;
  isExportDisabled: boolean;
  refreshSequences: () => Promise<void>;
  onFiltersClick?: () => void;
  showFilters?: boolean;
  debugData: string | null;
  onDebugClick?: () => Promise<void>;
  contactsLoadingState: LoadingState;
  isListSelected: boolean
  outreachConfigured: boolean;
  salesforceConfigured: boolean;
}

export default function ContactsHeader ({
  contactListTitle,
  contactsCount,
  warningsCount,
  searchQuery,
  onSearchQueryChange,
  onClearSearch,
  activeSource,
  onExportContacts,
  isExportDisabled,
  onFiltersClick,
  showFilters = false,
  refreshSequences,
  onDebugClick,
  contactsLoadingState,
  isListSelected,
  outreachConfigured,
  salesforceConfigured
}: ContactsHeaderProps): React.JSX.Element {
  const { t } = useTranslation();

  const { timeAgo, updateTimeAgoDate } = useTimeAgo();
  
  const renderTimeAgo = useCallback(()=>{
    if (!outreachConfigured || !isListSelected) {
      return null;
    }
    
    return (
      timeAgo !== undefined
          && <span className='text-xs'>
            {` • ${t('adminConsole.activeCallSessions.updated')} ${timeAgo}`}
          </span>);
  }, [outreachConfigured, timeAgo]);

  const renderIntegrationButton = (): React.JSX.Element | null => {
    const disableSalesforce = !salesforceConfigured;
    const disableOutreach = !outreachConfigured && !isListSelected;

    switch (activeSource) {
    case 'salesforce': return (
      <Button
        className='bg-primary hover:bg-primary/90 text-primary-foreground disabled:bg-muted cursor-pointer gap-2'
        disabled={disableSalesforce}
        onClick={() => {
          if (onDebugClick != null) {
            void onDebugClick();
          }
        }}
        title={t('contacts.header.debug')}
      >
        <IconBug className="h-4 w-4" />
        {t('contacts.header.debug')}
      </Button>
    );
    case 'outreach': return (
      <Button
        className='bg-primary hover:bg-primary/90 text-primary-foreground disabled:bg-muted cursor-pointer gap-2'
        disabled={disableOutreach}
        onClick={()=> {
          void refreshSequences();
          updateTimeAgoDate(new Date());
        }}
        title={t('contacts.header.sync')}
      >
        <RefreshCw className="h-4 w-4" />
        {t('contacts.header.sync')}
        {renderTimeAgo()}
      </Button>);
    default: return null;

    }
  };

  useEffect(() => {
    updateTimeAgoDate(new Date());
  }, []);

  return (
    <div className="rounded-md border border-border bg-card">
      {/* Stats Row */}
      <div className="flex flex-col gap-3 border-b border-border px-4 py-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap items-center gap-4">
          {/* List Name */}
          {contactListTitle !== null && contactListTitle.trim() !== '' && (
            <span className="text-sm font-semibold text-foreground">
              {contactListTitle}
            </span>
          )}

          {contactsLoadingState === LoadingState.SUCCESS && (
            <>
              {/* Contacts Count */}
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">{contactsCount}</span>
                <span>{t('contacts.header.contacts')}</span>
              </div>

              {/* Warnings Count */}
              {warningsCount > 0 && (
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                  <span>{warningsCount}</span>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {showFilters && onFiltersClick !== undefined && (
            <Button variant="outline" size="sm" className="gap-2" onClick={onFiltersClick}>
              <Filter className="h-4 w-4" />
              {t('contacts.header.filters')}
            </Button>
          )}

          {activeSource === 'csv' ? (
            <Button
              size="sm"
              className="bg-primary hover:bg-primary/90 text-primary-foreground cursor-pointer"
              onClick={onExportContacts}
              disabled={isExportDisabled}
              title={t('contacts.header.export')}
            >
              <Download className="mr-1 h-4 w-4" />
              {t('contacts.header.export')}
            </Button>
          ) : renderIntegrationButton()
          }
        </div>
      </div>

      {/* Search Row */}
      <div className="p-4">
        <SearchInput
          value={searchQuery}
          onChange={(event) => {
            onSearchQueryChange(event.target.value);
          }}
          onClear={onClearSearch}
          placeholder={t('contacts.header.searchPh')}
        />
      </div>
    </div>
  );
}
